<template>
  <div class="widget w-markets-constituents-widget" :class="['font-size-' + fontSize, { bold: isFontBold }]">
    <c-widget-header
      csvExport
      fieldsConfigManager="advanced"
      hideLinkModeReceive
      optimizeColumns
      performanceBar
      printView
      :wid="wid"
      @csv-export="onCsvExport"
      @optimize-columns="autoSizeColumns"
    >
      <slot name="buttons" slot="buttons" />
    </c-widget-header>

    <!-- Widget Menu -->
    <c-widget-control-bar>
      <v-form-group :label="i18n('controlbar.market.label')" label-cols="auto">
        <v-form-select
          :value="selectedMarket"
          :options="marketOptions"
          @input="onMarketSelected"
          :aria-label="i18n('controlbar.market.label')"
        />
      </v-form-group>
      <v-form-group :label="$t('white-label.globalLabels.template')" label-cols="auto">
        <v-form-select
          :options="fieldsConfigNameOptions"
          v-model="controlFieldsConfigName"
          :aria-label="$t('white-label.globalLabels.template')"
        />
      </v-form-group>
      <v-form-group :label="i18n('autosort', 'globalLabels')" label-cols="auto">
        <c-widget-input-switch
          :options="autosortOptions"
          v-model="controlAutosort"
          :aria-label="i18n('autosort', 'globalLabels')"
        />
      </v-form-group>
    </c-widget-control-bar>

    <!-- Widget Body -->
    <div class="widget-body">
      <c-streaming-error :show="hasStreamingError" />

      <c-advances-declines
        v-if="showPerformanceBar && !isError"
        :label="$t('white-label.widgets.portfolio.labels.advancesDeclines')"
        :advances="performanceBar.advances"
        :declines="performanceBar.declines"
        :totalAdvances="performanceBar.totalAdvances"
        :totalDeclines="performanceBar.totalDeclines"
        :totalNeutral="performanceBar.totalNeutral"
      />
      <c-loader v-if="isLoading" />
      <ag-grid-vue
        v-if="!isError"
        v-show="!isLoading"
        class="c-data-grid ag-theme-balham-dark"
        animateRows
        :asyncTransactionWaitMillis="gridUpdateInterval"
        :columnDefs="columnDefs"
        :context="this"
        :defaultColDef="defaultColumnDef"
        ensureDomOrder
        :fullWidthCellRenderer="loadingCellRenderer"
        :gridOptions="gridOptions"
        :rowHeight="agGridRowHeight"
        rowSelection="single"
        :suppressColumnVirtualisation="isAccessibilityEnabled"
        suppressContextMenu
        suppressDragLeaveHidesColumns
        suppressFieldDotNotation
        treeData
        treeDataDisplayType="custom"
        maintainColumnOrder="false"
        @cell-focused="onCellFocusedWidget"
        @column-moved="onColMoved"
        @column-resized="onColResized"
        @drag-stopped="onGridDragEnd"
        @grid-ready="onGridReady"
        @sort-changed="onTableSort"
        @new-columns-loaded="ev => setColumnState(ev.columnApi)"
        @async-transactions-flushed="onAsyncTransactionsFlushed"
      />
    </div>
  </div>
</template>

<script>
import agGridCommons from '@mixins/agGridCommons';
import CAdvancesDeclines from '@custom/CAdvancesDeclines';
import CLoader from '@custom/CLoader';
import CWidgetHeader from '@custom/widgetContainer/CWidgetHeader';
import CWidgetControlBar from '@custom/widgetContainer/CWidgetControlBar';
import CWidgetControlBarGroup from '@custom/widgetContainer/CWidgetControlBarGroup';
import CWidgetInputSwitch from '@custom/widgetContainer/CWidgetInputSwitch';
import CStreamingError from '@custom/CStreamingError';

import indicesSectorsList from '@/utils/indicesSectorsList.json';
import { getConsolidatedRic } from '@/utils/helpers';

import { API } from '@/state/api';
import { GenericTickerRenderer, RowButtonRenderer } from '@custom/dataGrid/renderers';
import { JSQuoteRenderer } from '@custom/dataGrid/renderers';

import { AgGridVue } from 'ag-grid-vue';

export default {
  name: 'w-markets-constituents-widget',
  mixins: [agGridCommons],
  components: {
    CAdvancesDeclines,
    CLoader,
    CWidgetHeader,
    CWidgetControlBar,
    CWidgetControlBarGroup,
    CWidgetInputSwitch,
    AgGridVue,
    RowButtonRenderer,
    GenericTickerRenderer,
    CStreamingError
  },
  props: {
    wid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isLoading: true,
      isError: false,
      hasStreamingError: false,
      gridSelectedNode: null,
      gridOptions: {
        getDataPath: data => {
          const path = [data['q.RIC']];
          if (data.group) path.unshift(data.group);
          return path;
        },
        getRowNodeId(data) {
          return data['q.RIC'];
        },
        isFullWidthCell: node => node.data.loadingCell,
        onRowGroupOpened: this.onRowGroupToggled,
        onRowSelected: e => {
          if (e.node.isSelected()) {
            this.updateLink(e);
          }
        },
        navigateToNextCell: params => {
          const KEY_UP = 38;
          const KEY_DOWN = 40;

          const nextCell = params.nextCellPosition;

          if ([KEY_UP, KEY_DOWN].includes(params.key)) {
            // version-specific implementation, ag-grid logic may change in the future making this code obsolete

            const previousCell = params.previousCellPosition;
            // prevent jump over detail row
            const diff = nextCell ? nextCell.rowIndex - previousCell.rowIndex : 2; //handle last detail row as opened
            if (Math.abs(diff) > 1) {
              const detailHeaderCell = this.$el.querySelector(
                `.ag-row[row-index="${previousCell.rowIndex + (diff > 0 ? 1 : -1)}"] .ag-header-cell`
              );
              if (detailHeaderCell) {
                detailHeaderCell.focus();
                this.gridOptions.api.clearFocusedCell();
                return null;
              }
            }
          }
          return nextCell;
        },
        columnTypes: { actionable: {} },
        tabToNextHeader: this.tabToNextHeader,
        tabToNextCell: this.tabToNextCell
      },
      selectedMarket: '',
      subscriptionsMap: {},
      tableFieldsPrepend: ['action']
    };
  },
  computed: {
    marketOptions() {
      return [
        { value: 'all_indices', text: this.i18n('controlbar.market.options.all-indices') },
        { value: 'ca_indices', text: this.i18n('controlbar.market.options.ca-indices') },
        { value: 'us_indices', text: this.i18n('controlbar.market.options.us-indices') },
        { value: 'ca_sectors', text: this.i18n('controlbar.market.options.ca-sectors') },
        { value: 'us_sectors', text: this.i18n('controlbar.market.options.us-sectors') }
        // { value: 'eu_indices', text: 'European Indices' }
      ];
    },
    extendedColDefs() {
      return {
        action: {
          cellRenderer: 'RowButtonRenderer',
          width: 40
        },
        'q._TCR': {
          cellRenderer: 'agGroupCellRenderer',
          cellRendererParams: {
            innerRendererFramework: 'GenericTickerRenderer',
            innerRendererParams: params => {
              if (params.data) {
                return {
                  ticker: params.data['q._TCR'],
                  tradeHaltValue: params.data['q.TRD_STATUS'],
                  priceChange: params.data['q._NETCHNG_1'],
                  groupSRText: this.i18n('toggleGroupExpanding', 'screenReaderLabels')
                };
              }
            },
            showRowGroup: true,
            suppressCount: true
          },
          type: ['actionable']
        },
        'q._BID': {
          cellRenderer: JSQuoteRenderer,
          cellClass: ['text-right', 'text-center'],
          tooltipValueGetter: this.tooltipValueGetter('q._BID'),
          type: ['actionable']
        },
        'q._ASK': {
          cellRenderer: JSQuoteRenderer,
          cellClass: ['text-right', 'text-center'],
          tooltipValueGetter: this.tooltipValueGetter('q._ASK'),
          type: ['actionable']
        }
      };
    },
    rics() {
      switch (this.selectedMarket) {
        case 'all_indices':
          return indicesSectorsList.indices.CA.concat(indicesSectorsList.indices.US).map(index => index.value);
        case 'ca_indices':
          return indicesSectorsList.indices.CA.map(index => index.value);
        case 'us_indices':
          return indicesSectorsList.indices.US.map(index => index.value);
        case 'ca_sectors':
          return indicesSectorsList.sectors.CA.map(sector => sector.value);
        case 'us_sectors':
          return indicesSectorsList.sectors.US.map(sector => sector.value);
        // EU Indices left for testing purposes, in case Americas exchanges are closed
        // case 'eu_indices':
        //   return indicesSectorsList.indices.EU.map(index => index.value);
      }
    }
  },
  watch: {
    detailColumnDefs() {
      this.detailCellRendererParams.detailGridOptions.columnDefs = this.detailColumnDefs;
      this.autoSizeColumns();
    },
    'widgetStore.market': {
      handler(val) {
        this.selectedMarket = val;
        this.fetch();
      },
      immediate: true
    }
  },
  methods: {
    getQuoteData(rics) {
      return API.Rest.get(`/quote/info?rics=${rics}&fids=${this.requestFids}`).then(response => {
        // if (response.data.data.length === 0) {
        //   throw new Error('NO_QUOTE_INFO_RECEIVED');
        // }
        Object.assign(
          this.formats,
          Object.keys(response.data.metadata).reduce((acc, fid) => {
            acc[fid] = response.data.metadata[fid].type;
            return acc;
          }, {})
        );
        // reset net change collection to prevent holding obsolete values
        this.netChanges = {};

        const snapshot = response.data.data;
        const rowsData = [];
        snapshot.forEach(data => {
          const rowData = this.hydrateData(data);
          // rowData.group = rowData['q.RIC'];
          rowsData.push(rowData);
          rowsData.push({
            'q.RIC': rowData['q.RIC'] + ':loader',
            group: rowData['q.RIC'],
            loadingCell: true
          });
        });
        this.gridOptions.api.setRowData(rowsData);
        this.isLoading = false;
        // bc ag-grid doesn't set aria-expanded attribute for treeData group rows, we need to fix it on rows render; needed mainly for screenreader
        setTimeout(() => {
          this.gridOptions.api.forEachNode(node => {
            if (node.allChildrenCount) {
              this.$el
                .querySelector(`div[row-id="${node.id}"] > .ag-cell[col-id="q._TCR"]`)
                .setAttribute('aria-expanded', false);
            }
          });
        }, 350);
      });
    },
    handleSnapshot(response, err, gridAPI) {
      if (err) {
        console.error('Streaming snapshot error', err);

        if (this.isLoading) {
          this.isError = true;
          this.isLoading = false;
        } else {
          this.hasStreamingError = true;
        }

        return;
      }

      this.handleUpdate(response, err, gridAPI);
    },
    handleUpdate(response, err) {
      if (err) {
        this.hasStreamingError = true;
        return console.error('Streaming update', err);
      }
      this.hasStreamingError = false;
      response = response.payload[0];
      let rowNode;
      try {
        rowNode = this.gridOptions.api.getRowNode(response.ric);
      } catch (e) {
        return;
      }
      // if there's no rowNode, then there's most probalby a running refetch, so don't try to update removed rows
      if (!rowNode) return;
      const updateData = this.hydrateData(response.data, rowNode.data, {
        // update netChanges only for group rows
        updateNetChanges: Boolean(!rowNode.data.group)
      });
      if (this.gridOptions && this.gridOptions.api) {
        this.gridOptions.api.applyTransactionAsync({ update: [updateData] }, result => {
          if (this.gridOptions && this.gridOptions.api)
            this.gridOptions.api.refreshCells({
              rowNodes: result.update,
              columns: ['q._TCR'],
              force: true
            });
        });
      }
    },
    onMarketSelected(market) {
      this.$store.commit(`widget/${this.wid}/MARKET_CHANGED`, market);
    },
    fetch() {
      this.isLoading = true;
      this.streamingUnsubscribe();
      // reset table on re-fetch to avoid cell renderers to attempt rendering before the data arrives
      if (this.gridOptions.api) this.gridOptions.api.setRowData([]);
      // can't user /metadata enpoint, as the streaming endpoint doesn't support sector ric
      this.getQuoteData(this.rics)
        .catch(err => {
          this.isLoading = false;
          this.isError = true;
          console.error(err);
        })
        .then(() => this.streamingSubscribe(this.rics));
    },
    loadingCellRenderer() {
      return `<div style="margin-left: 30px">${this.i18n('loader.label', 'components')}...</div>`;
    },
    onTableSort() {
      const sortState = this.getSortState();
      if (this.controlAutosort !== false) {
        this.$store.commit(`widget/${this.wid}/TABLE_SORT_CHANGED`, sortState);
      }
    },
    onCellFocusedWidget(params) {
      const column = params.column;
      if (!column || column.colId === 'q._TCR') return;
      return this.onCellFocused(params);
    },
    onRowGroupToggled(e) {
      this.gridOptions.api.forEachNode(node => {
        if (!node.allChildrenCount) return;
        if (e.node.expanded && node.expanded && node !== e.node) {
          node.setExpanded(false);
        }
        const cellElement = this.$el.querySelector(`div[row-id="${node.id}"] > .ag-cell[col-id="q._TCR"]`);
        if (cellElement) {
          cellElement.setAttribute('aria-expanded', e.node.expanded);
        }
      });
      if (e.node.expanded) {
        const ric = getConsolidatedRic(e.node.data['q.RIC']);
        API.Rest.get(`/index/constituents?ric=${ric}&fids=${this.requestFids}`)
          .then(response => {
            if (response.data.data.length === 0) return null;

            const rowsData = [];
            const rics = [];
            response.data.data.forEach(data => {
              const rowData = this.hydrateData(data, undefined, {
                updateNetChanges: false
              });
              rowData.group = e.node.data['q.RIC'];
              rowsData.push(rowData);
              rics.push(rowData['q.RIC']);
            });
            this.gridOptions.api.applyTransaction({
              add: rowsData,
              remove: [{ 'q.RIC': e.node.data['q.RIC'] + ':loader' }]
            });

            return rics;
          })
          .catch(error => console.error(error))
          .then(this.streamingSubscribe)
          .then(subId => (this.subscriptionsMap[e.node.data['q.RIC']] = subId));

        const cellElement = this.$el.querySelector(`div[row-id="${e.node.id}"] > .ag-cell[col-id="q._TCR"]`);
        if (cellElement) {
          const actionElement = cellElement.querySelector(
            'span.ag-group-expanded:not(.ag-hidden), span.ag-group-contracted:not(.ag-hidden)'
          );
          if (actionElement) actionElement.classList.add('active');
        }
      } else {
        this.gridOptions.api.applyTransaction({
          add: [
            {
              'q.RIC': e.node.data['q.RIC'] + ':loader',
              group: e.node.data['q.RIC'],
              loadingCell: true
            }
          ],
          remove: e.node.allLeafChildren.slice(1).map(node => ({ 'q.RIC': node.id }))
        });
        if (this.subscriptionsMap[e.node.data['q.RIC']]) {
          e.context.streamingUnsubscribe(this.subscriptionsMap[e.node.data['q.RIC']]);
        }
      }
    },
    onGridReady(ev) {
      this.onTableReady(ev, this.i18n('widget-title'));
    }
  },
  // Lifecycle
  beforeDestroy() {
    this.streamingUnsubscribe();
    if (this.gridOptions) this.gridOptions = null;
    if (this.gridApi) this.gridApi = null;
  }
};
</script>

<style lang="scss">
@import '~@/styles/widgets/markets-constituents-widget';
</style>
